import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Typography } from "@material-ui/core/index"
import { Link } from "gatsby-plugin-intl"
import InstagramLink from "./InstagramLink"
import { ACCENT, CTA_BUTTON_PRIMARY } from "../styles/cta"

const WARM_WHITE = "#f3e3cc"
const BODY = "#e9e0d4"

// Each stage is lightest right behind the head, the way the studio lit the
// portraits, so the finest hair strands blend in.
const STAGE_BASE = "#16100c"
const stageGlow = (x) =>
  `radial-gradient(ellipse 36% 64% at ${x} 36%, #a3754b 0%, #7a5536 26%, #402c1d 56%, #17110c 88%)`
const STAGE_PHONE =
  "radial-gradient(ellipse 90% 44% at 50% 20%, #a3754b 0%, #7a5536 30%, #402c1d 62%, #16100c 100%)"

const useStyles = makeStyles((theme) => {
  const phone = theme.breakpoints.down("xs")
  const stacked = theme.breakpoints.down("sm")
  const wide = theme.breakpoints.up("md")
  return {
    stage: {
      position: "relative",
      overflow: "hidden",
      background: "var(--stage-desktop)",
      [stacked]: { background: "var(--stage-phone)" },
      [wide]: { zoom: 0.8 },
    },
    inner: {
      position: "relative",
      maxWidth: 1280,
      margin: "0 auto",
      minHeight: 720,
      padding: theme.spacing(12, 4),
      display: "flex",
      alignItems: "center",
      [stacked]: { display: "block", minHeight: 0, padding: 0 },
    },
    innerAfterBar: { [wide]: { paddingTop: theme.spacing(12) + 72 } },
    cutout: {
      position: "relative",
      zIndex: 1,
      [wide]: {
        position: "absolute",
        bottom: 0,
        right: "2%",
        height: "min(88%, 660px)",
        width: "auto",
      },
      [stacked]: {
        display: "block",
        width: "100%",
        maxWidth: 520,
        height: "auto",
        margin: "0 auto",
        paddingTop: theme.spacing(5),
      },
    },
    cutoutFlip: { [wide]: { right: "auto", left: "2%" } },
    text: {
      position: "relative",
      zIndex: 2,
      width: "50%",
      display: "flex",
      flexDirection: "column",
      [stacked]: {
        width: "auto",
        marginTop: -120,
        padding: theme.spacing(10, 2.5, 7),
        background:
          "linear-gradient(180deg, transparent 0, var(--stage-base) 120px, var(--stage-base) 100%)",
      },
    },
    textFlip: { [wide]: { marginLeft: "auto" } },

    nameBlock: {
      paddingLeft: "1rem",
      borderLeft: `3px solid ${WARM_WHITE}`,
      margin: 0,
    },
    meet: {
      display: "block",
      color: WARM_WHITE,
      textTransform: "uppercase",
      letterSpacing: "0.32em",
      fontSize: "0.9rem",
      fontWeight: 500,
      marginBottom: theme.spacing(1.25),
    },
    // As large as fits the column on one line: "Susan Schmidt", the longer
    // name, is 6.3em wide in Helvetica Neue Light.
    name: {
      display: "block",
      color: "#fff",
      fontWeight: 300,
      letterSpacing: "-0.01em",
      lineHeight: 1,
      whiteSpace: "nowrap",
      fontSize: "min(7.2rem, calc((100vw - 72px) / 6.42))",
      textDecoration: "none",
      "&:hover": { textDecoration: "none", opacity: 0.9 },
      // Browsers disagree on how zoom scales vw, so the name opts out of the
      // section's zoom and is sized at 80% directly.
      [wide]: {
        zoom: 1.25,
        fontSize:
          "min(5.76rem, calc(((min(100vw, 1024px) - 51.2px) / 2 - 19.2px) / 6.42))",
      },
    },
    credential: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      marginTop: theme.spacing(3),
      color: ACCENT,
      textTransform: "uppercase",
      letterSpacing: "0.18em",
      fontSize: "0.8rem",
      fontWeight: 600,
    },
    bio: {
      color: BODY,
      fontSize: "1.15rem",
      lineHeight: 1.8,
      fontWeight: 300,
      margin: theme.spacing(3.5, 0, 0),
      [phone]: { fontSize: "1.08rem" },
    },
    listLabel: {
      color: WARM_WHITE,
      textTransform: "uppercase",
      letterSpacing: "0.18em",
      fontSize: "0.74rem",
      fontWeight: 600,
      margin: theme.spacing(3.5, 0, 1.25),
      opacity: 0.8,
    },
    list: {
      display: "flex",
      flexWrap: "wrap",
      gap: "6px 0",
      margin: 0,
      padding: 0,
      "& li": {
        listStyle: "none",
        color: "#fff",
        fontSize: "1.08rem",
        "&:not(:last-child)::after": {
          content: '"·"',
          color: ACCENT,
          margin: "0 12px",
        },
      },
    },
    cta: {
      ...CTA_BUTTON_PRIMARY,
      width: "auto",
      alignSelf: "flex-start",
      marginTop: theme.spacing(4.5),
      padding: "0 34px",
      minHeight: 60,
      [phone]: { alignSelf: "stretch" },
    },
    logosBand: {
      position: "relative",
      zIndex: 2,
      background: "var(--stage-base)",
      padding: theme.spacing(0, 4, 7),
      [phone]: { padding: theme.spacing(0, 2.5, 7) },
    },
    // Rows of four, two or one, always centred.
    logos: {
      maxWidth: 1280,
      margin: "0 auto",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "20px 32px",
      paddingTop: theme.spacing(3.5),
      borderTop: "1px solid rgba(243,227,204,0.12)",
      [phone]: { flexDirection: "column" },
    },
    logo: {
      width: "calc((100% - 96px) / 4)",
      maxWidth: 300,
      height: "auto",
      opacity: 0.85,
      [stacked]: { width: "calc((100% - 32px) / 2)" },
      [phone]: { width: 225 },
    },
    logoOnBlack: { mixBlendMode: "screen" },
  }
})

const DoctorsShowcase = ({
  doctors,
  t,
  specialtiesOf,
  bookingUrl,
  afterBar,
}) => {
  const c = useStyles()

  return (
    <section>
      {doctors.map((d, i) => {
        const flip = i % 2 === 1
        const style = {
          "--stage-base": STAGE_BASE,
          "--stage-desktop": stageGlow(flip ? "29%" : "71%"),
          "--stage-phone": STAGE_PHONE,
        }
        return (
          <article className={c.stage} style={style} key={d.id}>
            <div
              className={`${c.inner} ${
                afterBar && i === 0 ? c.innerAfterBar : ""
              }`}
            >
              <img
                src={d.cutout}
                width={d.cutoutSize[0]}
                height={d.cutoutSize[1]}
                loading="lazy"
                alt={t(`internationalPatients.why.${d.key}.portraitAlt`)}
                className={`${c.cutout} ${flip ? c.cutoutFlip : ""}`}
              />
              <div className={`${c.text} ${flip ? c.textFlip : ""}`}>
                <Typography component="h2" className={c.nameBlock}>
                  <span className={c.meet}>
                    {t(`home.doctors.${d.key}.meetPrefix`)}
                  </span>{" "}
                  <Link to={`/about/#${d.id}`} className={c.name}>
                    {t(`home.doctors.${d.key}.fullName`)}
                  </Link>
                </Typography>
                <span className={c.credential}>
                  {t(`home.doctors.${d.key}.title`)}
                  <InstagramLink
                    handle={d.instagram}
                    name={t(`internationalPatients.why.${d.key}.name`)}
                    color="#e6d5bd"
                    hoverColor="#fff"
                  />
                </span>
                <Typography component="p" className={c.bio}>
                  {t(`home.doctors.${d.key}.bio`)}
                </Typography>
                <Typography component="p" className={c.listLabel}>
                  {t(`home.doctors.${d.key}.specialtiesLabel`)}
                </Typography>
                <ul className={c.list}>
                  {specialtiesOf(d.key).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={c.cta}
                >
                  {t(`home.doctors.${d.key}.consultWith`)} →
                </a>
              </div>
            </div>
            <div className={c.logosBand}>
              <div className={c.logos}>
                {d.logos.map((logo) => (
                  <img
                    key={logo.alt}
                    src={logo.src}
                    alt={logo.alt}
                    className={
                      logo.onBlack ? `${c.logo} ${c.logoOnBlack}` : c.logo
                    }
                  />
                ))}
              </div>
            </div>
          </article>
        )
      })}
    </section>
  )
}

export default DoctorsShowcase
